import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { AnimatePresence } from 'motion/react';
import { OverlayFrame } from './OverlayFrame';
import { ModalShell } from './ModalShell';
import { SearchInput } from './SearchInput';
import { DataTable, type TableRow } from './DataTable';
import { Button } from './Button';
import {
  apiPost,
  type CloudFlowSummary,
  type ClassifyTriggerResult,
  type FlowTriggerType,
} from '../../api';

/**
 * FlowPickerReskin — the reskin's P1 ("Select one of your Automations").
 *
 * This is the Figma-look replacement for Share.tsx's hand-CSS flow picker. It renders the REAL Design
 * primitives (OverlayFrame + ModalShell + SearchInput + DataTable + Button) fed the backend's live
 * flows list, matching ~/FrontendFinal/Design/src/pages/UploadSelectPage.tsx pixel-for-pixel — three
 * columns (Name / Modified / Type), the teal search bar, Continue Draft / Cancel / Next footer.
 *
 * IT CHANGES NO LOGIC. Selecting a row and pressing Next calls the SAME `onPick` (Share.tsx's
 * `openFor`) the classic table's button does; Cancel closes; the flows list, its filter and the
 * connector pre-detect handoff are Share.tsx's, untouched.
 *
 * WHY A STANDALONE FRAME. The rest of the reskin overlay is driven by `useUploadOverlay` (which owns
 * the dialogRef + Tab/Escape trap). The picker runs BEFORE that overlay opens, so it owns a LOCAL
 * dialogRef and a minimal Escape-to-close onKeyDown, handed to the same OverlayFrame the overlay uses.
 * Every visible pixel of the frame is therefore identical to the overlay it hands off to.
 *
 * COLUMNS.
 *   - Name, Modified come from the flows LIST call (`/api/flows/mine`) — instant.
 *   - Modified is the flow's `modifiedOn` (Dataverse `modifiedon`) formatted to the design's relative
 *     string ("5 h ago", "2 wk ago"). Absolute → relative is done here; the design draws no timestamp.
 *   - Type is Power Automate's Automated/Instant/Scheduled trigger classification, which needs one
 *     `fetchWorkflow` per flow. So it LOADS LAZILY: the table opens with Type blank ("—") and each
 *     cell fills as its `/api/tools/classify-trigger` fetch resolves. `'Unknown'` (unreadable
 *     blueprint) also renders blank.
 */

/** The design's relative-time units, matching the P1 fixture ("2 wk ago", "3 mth ago", "5 h ago"). */
const RELATIVE_UNITS: { limit: number; div: number; label: string }[] = [
  { limit: 60, div: 1, label: 'min' }, // < 60 min → "N min ago"
  { limit: 24 * 60, div: 60, label: 'h' }, // < 24 h → "N h ago"
  { limit: 7 * 24 * 60, div: 24 * 60, label: 'd' }, // < 7 d → "N d ago"
  { limit: 30 * 24 * 60, div: 7 * 24 * 60, label: 'wk' }, // < ~1 mth → "N wk ago"
  { limit: 365 * 24 * 60, div: 30 * 24 * 60, label: 'mth' }, // < 1 y → "N mth ago"
];

/**
 * Format a Dataverse `modifiedon` ISO string to the design's relative string. `nowMs` is passed in
 * (not read from `Date.now()` inside a fixture-derived component) so the format is testable and the
 * caller controls the clock. Returns '—' for a null/unparseable value or a future timestamp.
 */
export function formatRelative(iso: string | null, nowMs: number): string {
  if (!iso) return '—';
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return '—';
  const minutes = Math.floor((nowMs - then) / 60000);
  if (minutes < 0) return '—';
  if (minutes < 1) return 'just now';
  for (const u of RELATIVE_UNITS) {
    if (minutes < u.limit) {
      const n = Math.max(1, Math.floor(minutes / u.div));
      return `${n} ${u.label} ago`;
    }
  }
  const years = Math.max(1, Math.floor(minutes / (365 * 24 * 60)));
  return `${years} y ago`;
}

/** How many classify-trigger fetches run at once — keep the picker's Dataverse pressure modest. */
const CLASSIFY_CONCURRENCY = 5;

export interface FlowPickerReskinProps {
  /** The signed-in user's cloud flows (already loaded — `/api/flows/mine`). */
  flows: CloudFlowSummary[];
  /** The environment the flows live in — sent with each classify-trigger request. */
  environmentId: string;
  /** Which of the listed flows already have a saved draft (drives the Continue Draft affordance). */
  draftedIds: Set<string>;
  /** Chosen row → advance into the overlay (Share.tsx's `openFor`). */
  onPick: (flow: CloudFlowSummary) => void;
  /** Dismiss the picker (Cancel / Escape / backdrop). */
  onClose: () => void;
}

export function FlowPickerReskin({
  flows,
  environmentId,
  draftedIds,
  onPick,
  onClose,
}: FlowPickerReskinProps) {
  const [query, setQuery] = useState('');
  /** The selected row's workflowId (DataTable owns hover/selection and reports id|null). */
  const [selectedId, setSelectedId] = useState<string | null>(null);
  /** Lazily-filled Type per workflowId. Absent = still loading (cell shows '—'). */
  const [triggerTypes, setTriggerTypes] = useState<Map<string, FlowTriggerType>>(new Map());

  // The local focus-trap target for OverlayFrame (the overlay's own comes from useUploadOverlay; the
  // picker predates that, so it owns one). OverlayFrame focuses it on mount via its ref callback.
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') onClose();
  };

  // One clock read for the whole render pass, so every "N h ago" is measured against the same instant.
  // Read via an effect, not in render: Date.now() is impure, and calling it during render trips the
  // React purity rule (and could yield unstable timestamps across re-renders). Lazy-initialised once
  // at mount, then re-read when the flows list identity changes (a fresh picker open).
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    setNowMs(Date.now());
  }, [flows]);

  // ── Lazy Type: classify every listed flow, capped concurrency, out-of-order-safe ────────────────
  //
  // Fires once per picker mount over the FULL flows list (not the filtered view) so a later search
  // never re-triggers fetches. Each result is merged into the map as it lands. The effect's cleanup
  // flag drops any reply that arrives after unmount, so we never setState on a gone component.
  useEffect(() => {
    let alive = true;
    const queue = flows.map((f) => f.workflowId);
    let cursor = 0;

    async function worker() {
      while (alive && cursor < queue.length) {
        const id = queue[cursor++];
        if (!id) continue;
        const res = await apiPost<ClassifyTriggerResult>('/api/tools/classify-trigger', {
          targetEnvironmentId: environmentId,
          workflowId: id,
        });
        if (!alive) return;
        if (res.kind === 'ok') {
          const triggerType = res.body.triggerType;
          setTriggerTypes((prev) => {
            const next = new Map(prev);
            next.set(id, triggerType);
            return next;
          });
        }
        // A non-ok classify (consent/env/blueprint hiccup) simply leaves the cell blank — Type is
        // decorative context in the picker, never a gate on picking a flow.
      }
    }

    const workers = Array.from({ length: Math.min(CLASSIFY_CONCURRENCY, queue.length) }, worker);
    void Promise.all(workers);
    return () => {
      alive = false;
    };
  }, [flows, environmentId]);

  /** Flows filtered by the search box (client-side, over the same list), then mapped to table rows. */
  const rows = useMemo<TableRow[]>(() => {
    const q = query.trim().toLowerCase();
    const matched = q ? flows.filter((f) => f.name.toLowerCase().includes(q)) : flows;
    return matched.map((f) => {
      const t = triggerTypes.get(f.workflowId);
      return {
        id: f.workflowId,
        name: f.name,
        modified: formatRelative(f.modifiedOn, nowMs),
        // 'Unknown' (unreadable blueprint) reads as blank, same as still-loading.
        type: t && t !== 'Unknown' ? t : '—',
      };
    });
  }, [flows, triggerTypes, query, nowMs]);

  const selectedFlow = selectedId ? (flows.find((f) => f.workflowId === selectedId) ?? null) : null;
  const selectedHasDraft = selectedFlow ? draftedIds.has(selectedFlow.workflowId) : false;

  return (
    <OverlayFrame dialogRef={dialogRef} onKeyDown={onKeyDown} onClose={onClose}>
      <AnimatePresence mode="wait" initial={false}>
        <ModalShell
          key="picker"
          labelledBy="upload-select-title"
          headerInset="title"
          // bodyScroll={false}: the search bar must stay pinned at the top and only the
          // table section scrolls. With the default bodyScroll={true} the body is a
          // single overflow-y-auto block, so scrolling the table also scrolls the search
          // bar out of view and hides the filter button. Making the body a flex column
          // lets the search row be stationary while only the table container scrolls.
          bodyScroll={false}
          header={
            <div className="flex flex-wrap items-center justify-between gap-7">
              <h1 id="upload-select-title" className="min-w-0 text-xl font-light text-text">
                Select one of your Automations
              </h1>
              {/* No device-upload path exists in this flow (the backend shares Power Automate cloud
                  flows, not files), so this stays inert — matching the design's own TODO. */}
              <Button variant="outline-square" size="sm" state="disabled" className="shrink-0">
                Upload from device
              </Button>
            </div>
          }
          footer={
            <div className="border-t border-ink">
              <div className="flex flex-wrap items-center justify-between gap-7 px-modal-x py-11 pr-modal-footer-r">
                {/* Continue Draft advances the SELECTED flow only when it has a saved draft — the
                    overlay then routes to Resume. Disabled until such a row is picked. */}
                <Button
                  variant="outline-square"
                  size="xs"
                  state={selectedFlow && selectedHasDraft ? 'default' : 'disabled'}
                  onClick={() => {
                    if (selectedFlow && selectedHasDraft) onPick(selectedFlow);
                  }}
                >
                  Continue Draft
                </Button>
                <div className="flex items-center gap-modal-footer">
                  <Button variant="outline-square" size="xs" onClick={onClose}>
                    Cancel
                  </Button>
                  {/* Next opens the overlay on the chosen flow — the SAME handoff the classic table's
                      button makes. Disabled (measured grey state) until a row is selected. */}
                  <Button
                    variant="outline-square"
                    size="xs"
                    state={selectedFlow ? 'default' : 'disabled'}
                    onClick={() => {
                      if (selectedFlow) onPick(selectedFlow);
                    }}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </div>
          }
        >
          {/* Search bar — pinned at the top; does not scroll with the table. */}
          <div className="shrink-0 flex items-center gap-7 pt-11">
            <SearchInput
              size="xs"
              block
              value={query}
              onChange={setQuery}
              label="Search your flows"
            />
            {/* The design draws no filter panel, so what this would reveal is unspecified — inert. */}
            <Button variant="outline-square" size="xs" state="disabled" className="shrink-0">
              Filter
            </Button>
          </div>

          {/* Table section — scrolls when the flow list exceeds the available height. */}
          <div className="min-h-0 flex-1 overflow-y-auto scrollbar-none pt-11 pb-11">
            {rows.length === 0 ? (
              <p className="text-sm font-light text-text-inactive">
                No flows match "{query.trim()}".
              </p>
            ) : (
              <DataTable rows={rows} onSelect={setSelectedId} />
            )}
          </div>
        </ModalShell>
      </AnimatePresence>
    </OverlayFrame>
  );
}
